import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Button, Card, CardBody, CardHeader, CardFooter } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { getConsumptionByEstablishment, getFuelSupplySummaryByEstablishment } from '../../../../../slices/fuelSupply/thunk';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { getProductiveCycleActive } from '../../../../../slices/thunks';

import moment from 'moment';
import 'moment/locale/es'; // Importar el idioma español
import BarChart from '../../../../../Components/Common/Charts/BarChart';
moment.locale('es'); // Configurar el idioma



const prepareChartData = (data, colorsChart, filter) => {
    if (!Array.isArray(data) || data.length === 0) {
        //console.warn("prepareChartData: Recibió data vacía o incorrecta.");
        return { series: [], categories: [], colors: [] };
    }

    const dateFormat = filter === "1M" ? "YYYY-[S]WW" : ["3M", "6M", "1Y", "AC"].includes(filter) ? "MM-YYYY" : "DD-MM-YYYY";

    // Agrupamos por fecha y tipo de combustible, manteniendo el mayor balance por estanque
    const groupedData = data.reduce((acc, item) => {
        if (!item.supply_date || !item.fuel_type_info || !item.fuel_tank_info || !item.balance) {
            //console.warn("prepareChartData: Elemento con datos incompletos.", item);
            return acc;
        }

        const date = moment(item.supply_date).format(dateFormat);
        const fuelType = item.fuel_type_info?.name || "Desconocido";
        const tankName = item.fuel_tank_info?.name || "Sin nombre";
        const balance = Number(item.balance) || 0;

        const key = `${date}-${fuelType}-${tankName}`;

        // Guardamos solo el mayor balance del estanque en esa fecha
        if (!acc[key] || balance > acc[key].balance) {
            acc[key] = { date, fuelType, balance };
        }

        return acc;
    }, {});

    // Sumamos los balances mayores de cada estanque por tipo de combustible y fecha
    const aggregatedData = Object.values(groupedData).reduce((acc, item) => {
        const key = `${item.date}-${item.fuelType}`;
        if (!acc[key]) {
            acc[key] = { date: item.date, fuelType: item.fuelType, balance: 0 };
        }
        acc[key].balance += item.balance; // Sumamos solo los balances mayores
        return acc;
    }, {});

    const sortedData = Object.values(aggregatedData).sort((a, b) => moment(a.date, dateFormat).diff(moment(b.date, dateFormat)));

    if (sortedData.length === 0) {
        //console.warn("prepareChartData: No se encontraron datos agrupados.");
        return { series: [], categories: [], colors: [] };
    }

    // Fechas únicas en orden cronológico
    const categories = [...new Set(sortedData.map(item => item.date))];

    // Lista de tipos de combustible únicos
    const fuelTypes = [...new Set(sortedData.map(item => item.fuelType))];

    // Mapeo de colores para cada tipo de combustible
    const fuelTypeColorMap = fuelTypes.reduce((acc, fuel, index) => {
        acc[fuel] = colorsChart[index % colorsChart.length];
        return acc;
    }, {});

    // Construcción de series con los valores de balance sumado por tipo de combustible
    const series = fuelTypes.map(fuelType => {
        return {
            name: fuelType,
            data: categories.map(date => {
                const record = sortedData.find(item => item.date === date && item.fuelType === fuelType);
                return record ? record.balance : 0;
            })
        };
    });

    return {
        series,
        categories,
        colors: fuelTypes.map(fuel => fuelTypeColorMap[fuel])
    };
};




const getUniqueUnits = (data) => {
    if (!Array.isArray(data) || data.length === 0) return "N/A";

    const uniqueUnits = new Set(
        data
            .map(item => item.unit_measurement_info?.name?.trim()) // Acceder correctamente a `unit_measurement_info`
            .filter(Boolean) // Elimina valores vacíos, null o undefined
            .map(unit => unit.charAt(0).toUpperCase() + unit.slice(1)) // Capitalizar solo si hay datos
    );

    return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(", ") : "N/A";
};


const tooltipFormatter = (data) => {
    const extra = data?.series?.extra || {};
    if (!extra) return "";

    const { barge, stock, capacity, unit, tankName, fuelType } = extra;

    return `
        <div style="
            border-radius: 8px;
            padding: 10px;
            font-size: 11px; 
            text-align: left;
            max-width: 200px;">
            <div>Nombre: <span style="font-weight: bold;">${tankName}</span></div>
            ${barge !== "No aplica" ? `<div>Artefacto Naval: <span style="font-weight: bold;">${barge}</span></div>` : ""}
            <div>Capacidad: <span style="font-weight: bold;">${capacity.toLocaleString()} ${unit}</span></div>
            <div>Saldo: <span style="font-weight: bold;">${stock.toLocaleString()} ${unit}</span></div>
            <div>Tipo Combustible: <span style="font-weight: bold;">${fuelType}</span></div>
        </div>`;
};


const FuelTankInfoBalance = ({ establishment, title, dataColors, um = '', minHeight }) => {
    const dispatch = useDispatch();
    const [selectedFilter, setSelectedFilter] = useState('1M');

    const colorsChart = useMemo(() => getChartColorsArray(dataColors), [dataColors]);
    const [chartData, setChartData] = useState({ series: [], categories: [], fuelSummary: [] });

    const selectorFuelSupply = createSelector(
        state => state.FuelSupply,
        (_, type) => type,
        (data, type) => ({
            info: data.fuelSupplyByEstablishment[type] || null, // Acceder a los datos correctamente
            loading_tank: data.isLoadingList,
        })
    );

    // Obtener datos en el componente
    const { info, loading_tank } = useSelector(state => selectorFuelSupply(state, 'stock'));

    useEffect(() => {
        if (establishment?._id && selectedFilter) {
            dispatch(getConsumptionByEstablishment({
                establishment_id: establishment._id,
                filter: selectedFilter,
                type: 'stock',
            }));
        }
    }, [establishment]); // Asegurar dependencias correctas



    const selectedFilterRef = useRef(selectedFilter); // Mantiene el valor anterior del filtro

    const handleFilterChange = (filter) => {
        console.log(1)
        if (selectedFilterRef.current === filter) return; // Evita llamadas duplicadas

        setSelectedFilter(filter);
        selectedFilterRef.current = filter; // Actualiza el ref inmediatamente

        if (establishment?._id) {
            dispatch(getConsumptionByEstablishment({
                establishment_id: establishment._id,
                filter, // Usa el nuevo valor directamente
                type: 'stock',
            }));
        }
    };


    const yAxisUnits = getUniqueUnits(info?.data);

    useEffect(() => {
        if (Array.isArray(info?.data) && info?.data.length > 0 && colorsChart.length > 0) {
            setChartData(prepareChartData(info?.data, colorsChart, selectedFilter));
        } else {
            console.warn("useEffect: Data está vacía o colorsChart no está definido.");
        }
    }, [info?.data, colorsChart, selectedFilter]);




    if (info?.data?.length == 0) {
        return (
            <Card className='p-0 mb-2'>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="flex-grow-1 m-0">{title}</h5>
                </CardHeader>
                <CardBody className='p-2'>
                    <div className="d-flex justify-content-center align-items-center p-3">
                        <div className="text-center">
                            <div className="avatar-md">
                                <span className="avatar-title bg-info-subtle rounded-circle fs-1">
                                    <i className="ri-gas-station-fill text-info"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </CardBody>
                <CardFooter className="m-0 p-2 text-center">
                    <h6 className="mb-0 p-0 text-wrap w-100">
                        <span className="fs-11 badge bg-info-subtle text-info text-wrap">
                            No cuenta con registro de consumo de combustible
                        </span>
                    </h6>
                </CardFooter>
            </Card>
        );
    }


    return (
        <React.Fragment>
            <Card className='m-0 h-100'>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
                    <div className='btn-box-dashboard'>
                        {/* <Button
                            color={selectedFilter === 'AC' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === 'AC' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('AC')}
                            title="Ciclo Activo"
                        >
                            CA
                        </Button> */}
                        <Button
                            color={selectedFilter === '1Y' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '1Y' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('1Y')}
                            title="1 Año"
                        >
                            1A
                        </Button>
                        <Button
                            color={selectedFilter === '6M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '6M' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('6M')}
                            title="6 Meses"
                        >
                            6M
                        </Button>
                        <Button
                            color={selectedFilter === '3M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '3M' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('3M')}
                            title="3 Meses"
                        >
                            3M
                        </Button>
                        <Button
                            color={selectedFilter === '1M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '1M' ? "btn-soft-primary" : "btn-soft-secondary"}
                            onClick={() => handleFilterChange('1M')}
                            title="1 Mes"
                        >
                            1M
                        </Button>
                    </div>
                </CardHeader>


                {/* Gráfico de stock de combustible */}
                <CardBody className="p-0">
                    <BarChart
                        seriesChart={chartData.series}
                        categoriesChart={chartData.categories}
                        colorsChart={chartData.colors}
                        toolbarChart={true}
                        height={minHeight}
                        legendPosition={'bottom'}
                        legendOffsetY={2}
                        strokeCurve={'smooth'}
                        chartType={'area'}
                        horizontal={false}
                        yAxisTitle={yAxisUnits}
                        um={um}
                        tooltipFormatter={tooltipFormatter}
                    />
                </CardBody>
            </Card>
        </React.Fragment>
    );
};

export default FuelTankInfoBalance;
